import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "react-calendar/dist/Calendar.css";
import "../../assets/css/common.css";
import "../../assets/css/Profile.css";
import "../../assets/css/tooltip.css";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { updateClintData } from "../../redux/clientBookingForm";
import { FaEdit } from "react-icons/fa";
import { getAllEventOptions, updateAllEventOptions } from "../../API/FormEventOptionsAPI";
import { getAllDeliverableOptions, updateAllDeliverableOptions } from "../../API/FormDeliverableOptionsAPI";
import {
  Button,
  Input,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { LuPlus } from "react-icons/lu";

function FormOptions() {
 
  const [eventOptionsKeyValues, setEventOptionsKeyValues] = useState(null);
  const [deliverableOptionsKeyValues, setDeliverableOptionsKeyValues] = useState(null);
  const [addNewValue, setAddNewValue] = useState(null);
  const [saving, setSaving] = useState(false);

  /*
   * The option editor's whole state lives in one object, set fresh every time
   * an editor opens: which catalog, which key, and the rows being edited.
   *
   * It used to be spread across four variables. A "deliverable mode" flag was
   * set when a deliverable editor opened and never cleared, so a later event
   * editor added, deleted and saved into the deliverable catalog instead -
   * and photographers, cinematographers and drones exist in both, so the
   * wrong catalog was silently corrupted (QA finding ADMIN-01).
   *
   * Each row carries a rowKey: its database _id, or a temporary key for a row
   * added in this session. Rows used to be deleted by _id, and every unsaved
   * row's _id was undefined - so deleting one new option deleted them all
   * (QA finding ADMIN-02).
   */
  const [editor, setEditor] = useState(null);
  const tempKey = useRef(0);
  const newRowKey = () => `new-${++tempKey.current}`;

  const eventOptionObjectKeys = ["travelBy", "shootDirector", "photographers", "cinematographers", "drones", "sameDayPhotoEditors", "sameDayVideoEditors"]
  const deliverablePreWeddingOptionObjectKeys = ["assistants", "photographers", "cinematographers", "drones"]
  const deliverableAlbumOptionObjectKeys = ["albums"]
  const deliverableOptionObjectKeys = ["promos", "longFilms", "reels", "hardDrives", "performanceFilms"]

  const customStyles = {
    option: (defaultStyles, state) => ({
      ...defaultStyles,
      color: state.isSelected ? "white" : "black",
      backgroundColor: state.isSelected ? "rgb(102, 109, 255)" : "#EFF0F5",
    }),
    control: (defaultStyles) => ({
      ...defaultStyles,
      backgroundColor: "#EFF0F5",
      padding: "2px",
      border: "none",
      boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.15)",
    }),
    singleValue: (defaultStyles) => ({ ...defaultStyles, color: "#666DFF" }),
  };

  const target = useRef(null);

  const catalogFor = (catalog) =>
    catalog === "deliverable" ? deliverableOptionsKeyValues : eventOptionsKeyValues;

  const openEditor = (catalog, key) => {
    const source = catalogFor(catalog)?.[key];
    if (!source) return;
    setAddNewValue(null);
    setEditor({
      catalog,
      key,
      label: source.label,
      rows: (source.values || []).map((option) => ({
        ...option,
        rowKey: option._id ? String(option._id) : newRowKey(),
      })),
    });
  };

  const closeEditor = () => {
    setEditor(null);
    setAddNewValue(null);
  };

  const addOption = () => {
    const text = (addNewValue || "").trim();
    if (!text) return;
    setEditor((current) => ({
      ...current,
      rows: [...current.rows, { label: text, value: text, rowKey: newRowKey() }],
    }));
    setAddNewValue(null);
  };

  const deleteOption = (rowKey) =>
    setEditor((current) => ({
      ...current,
      rows: current.rows.filter((row) => row.rowKey !== rowKey),
    }));

  // Existing options were shown as editable inputs with no change handler, so
  // typing into them did nothing (QA finding ADMIN-06). Label and value stay
  // identical, as they are for every option this screen creates.
  const renameOption = (rowKey, text) =>
    setEditor((current) => ({
      ...current,
      rows: current.rows.map((row) =>
        row.rowKey === rowKey ? { ...row, label: text, value: text } : row
      ),
    }));

  const getAllFormOptionsHandler = async () => {
    const eventOptions = await getAllEventOptions();
    const deliverableOptions = await getAllDeliverableOptions();

    setEventOptionsKeyValues(eventOptions)
    setDeliverableOptionsKeyValues(deliverableOptions)
  }

  useEffect(() => {
    getAllFormOptionsHandler()
  },[])

  // Saving happens here only, from the Update button. It used to run from an
  // effect watching whether the modal was open - which also fired when the
  // page first loaded, sending an empty save (QA finding ADMIN-03).
  const saveEditor = async () => {
    const values = editor.rows.map(({ rowKey, ...option }) => ({
      ...option,
      label: String(option.label ?? "").trim(),
      value: String(option.value ?? "").trim(),
    }));
    if (values.some((option) => !option.value)) {
      window.notify("Options cannot be blank.", "error");
      return;
    }
    const seen = new Set();
    const duplicate = values.find((option) => {
      const k = option.value.toLowerCase();
      if (seen.has(k)) return true;
      seen.add(k);
      return false;
    });
    if (duplicate) {
      window.notify(`"${duplicate.value}" appears more than once.`, "error");
      return;
    }

    const catalog = catalogFor(editor.catalog);
    const updated = { ...catalog, [editor.key]: { ...catalog[editor.key], values } };
    setSaving(true);
    try {
      if (editor.catalog === "deliverable") {
        await updateAllDeliverableOptions(updated);
      } else {
        await updateAllEventOptions(updated);
      }
      window.notify("Options successfully updated", "success");
      closeEditor();
      // Reload so newly added options pick up their database ids.
      await getAllFormOptionsHandler();
    } catch (error) {
      // Keep the editor open with the changes, so nothing is lost.
      window.notify(
        error?.response?.data?.message ||
          "Could not save the options. Your changes are still here - try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    
      <div className="mt18">
        <Form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Row>
            {eventOptionObjectKeys.map((Objkey) => 
              <Col key={Objkey} xs="12" sm="6">
                <div className="mt25">
                  <div className="Text16N" style={{ marginBottom: "6px" }}>
                    {eventOptionsKeyValues && eventOptionsKeyValues[Objkey].label}
                  </div>
                  <div className="d-flex align-items-center">
                    <Select
                      // ref={travelSelect}
                      name="travelBy"
                      className="w-50 "
                      styles={customStyles}
                      options={eventOptionsKeyValues && eventOptionsKeyValues[Objkey].values}
                      required={true}
                    />
                    <FaEdit className=" fs-5 cursor-pointer" style={{ marginLeft: "10px" }} onClick={() => openEditor("event", Objkey)}/>
                    </div>
                </div>
              </Col>
            )}
          </Row>
        </Form>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="mt25">
            <div className="Text16N" style={{ marginTop: "6px" }}>
              <h4>Deliverables</h4>
            </div>
          </div>
          {
            <>
              <p className=" text16N mb-0 fw-bold">For Pre-Wedding:</p>
              <Row>
                {deliverablePreWeddingOptionObjectKeys.map((Objkey) => 
                  <Col key={Objkey} xs="12" sm="3">
                    <div className="mt25">
                      <div className="Text16N" style={{ marginBottom: "6px" }}>
                        {deliverableOptionsKeyValues && deliverableOptionsKeyValues[Objkey].label}
                      </div>
                      <div className="d-flex align-items-center">
                        <Select
                          // ref={travelSelect}
                          name="travelBy"
                          className="w-50"
                          styles={customStyles}
                          options={deliverableOptionsKeyValues && deliverableOptionsKeyValues[Objkey].values}
                          required={true}
                        />
                        <FaEdit className=" fs-5 cursor-pointer" style={{ marginLeft: "10px" }} onClick={() => openEditor("deliverable", Objkey)}/>
                        </div>
                    </div>
                  </Col>
                )}
              </Row>
            </>
          }

          <Row>
            {deliverableAlbumOptionObjectKeys.map((Objkey) => 
              <Col key={Objkey} xs="12" sm="3">
                <div className="mt25">
                  <div className="Text16N" style={{ marginBottom: "6px" }}>
                    {deliverableOptionsKeyValues && deliverableOptionsKeyValues[Objkey].label}
                  </div>
                  <div className="d-flex align-items-center">
                    <Select
                      // ref={travelSelect}
                      name="travelBy"
                      className="w-50"
                      styles={customStyles}
                      options={deliverableOptionsKeyValues && deliverableOptionsKeyValues[Objkey].values}
                      required={true}
                    />
                    <FaEdit className=" fs-5 cursor-pointer" style={{ marginLeft: "10px" }} onClick={() => openEditor("deliverable", Objkey)}/>
                    </div>
                </div>
              </Col>
            )}
          </Row>
          <Row style={{ marginBottom: "100px" }}>
            {deliverableOptionObjectKeys.map((Objkey) => 
              <Col key={Objkey} xs="12" sm="3">
                <div className="mt25">
                  <div className="Text16N" style={{ marginBottom: "6px" }}>
                    {deliverableOptionsKeyValues && deliverableOptionsKeyValues[Objkey].label}
                  </div>
                  <div className="d-flex align-items-center">
                    <Select
                      // ref={travelSelect}
                      name="travelBy"
                      className="w-50"
                      styles={customStyles}
                      options={deliverableOptionsKeyValues && deliverableOptionsKeyValues[Objkey].values}
                      required={true}
                    />
                    <FaEdit className=" fs-5 cursor-pointer" style={{ marginLeft: "10px" }} onClick={() => openEditor("deliverable", Objkey)}/>
                    </div>
                </div>
              </Col>
            )}
          </Row>
        </Form>
      </div>
      <Modal isOpen={Boolean(editor)} centered={true} size="md" fullscreen="md">
        <ModalHeader>{editor?.label}</ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <ModalBody>
            <div style={{height: "300px", overflow: "scroll"}}>
            {editor?.rows.map((option) =>
              <Row ref={target} key={option.rowKey}>
                <Col  xl="6" sm="6" className="mt-3 d-flex justify-content-center ">
                  <Input
                    type="text"
                    name={option.label}
                    className="forminput w-75"
                    value={option.value ?? ""}
                    placeholder={"Value..."}
                    onChange={(e) => renameOption(option.rowKey, e.target.value)}
                  />
                </Col>
                <Col xl="6" sm="6" className="mt-3 d-flex justify-content-center">
                  <Button type="button" color="danger" onClick={() => deleteOption(option.rowKey)}>
                    Delete
                  </Button>
                 </Col>
              </Row>
            )}
            </div>
            <Row ref={target} className="d-flex align-items-center justify-content-center">
              <Col  xl="6" sm="6" className="mt-3 d-flex justify-content-center" >
                <Input
                  type="text"
                  name="eventType"
                  className="forminput w-75"
                  value={addNewValue || ""}
                  placeholder={"Value..."}
                  onChange={(e) => setAddNewValue(e.target.value)}
                />
              </Col>
              <Col xl="6" sm="6" className="mt-3 d-flex justify-content-center">
                <div
                  className="fs-3 mx-1 d-flex cursor-pointer justify-content-center align-items-center"
                  onClick={addOption}
                  style={{
                    backgroundColor: "rgb(102, 109, 255)",
                    color: "white",
                    width: "30PX",
                    height: "30px",
                    borderRadius: "100%",
                  }}
                >
                  <LuPlus />
                 </div>
             </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline disabled={saving} onClick={closeEditor}>
              Cancel
            </Button>
            <Button className="Update_btn" disabled={saving} onClick={saveEditor}>
              {saving ? "Saving..." : "Update"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  );
}

export default FormOptions;
